/**
 * useVideoRoom - Core hook for LiveKit room connection
 * Manages connection lifecycle, participant state, and E2E encryption
 *
 * E2E Encryption (E2EE):
 * - Required for BMV-Ä compliance
 * - Uses LiveKit's built-in E2EE with shared passphrase
 * - All participants must use the same encryption key
 */

import { useState, useCallback, useEffect, useRef } from "react";
import {
  Room,
  RoomEvent,
  ConnectionState,
  ConnectionQuality,
  LocalParticipant,
  RemoteParticipant,
  Participant,
  ExternalE2EEKeyProvider,
  VideoPresets,
  type VideoCodec,
} from "livekit-client";

export type UserRole = "gutachter" | "versicherter" | "guest";

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export interface E2EEOptions {
  /** Shared encryption key/passphrase (must be same for all participants) */
  sharedKey: string;
  /** Optional: Custom worker URL. If not provided, uses livekit-client default */
  workerUrl?: string;
}

export interface UseVideoRoomOptions {
  serverUrl: string;
  token: string;
  role: UserRole;
  /**
   * Force legacy LiveKit signaling path (/rtc instead of /rtc/v1).
   * Use for OpenVidu/LiveKit deployments that do not expose v1 signaling yet.
   */
  forceLegacySignalPath?: boolean;
  /** E2E encryption options. If provided, E2EE will be enabled */
  e2ee?: E2EEOptions;
  /**
   * Always request the highest simulcast layer from remote participants,
   * regardless of viewport size. Disables adaptive streaming on the
   * subscriber side. Useful for the examiner who needs maximum video
   * quality for medical assessments.
   */
  preferHighQuality?: boolean;
  onParticipantJoined?: (participant: RemoteParticipant) => void;
  onParticipantLeft?: (participant: RemoteParticipant) => void;
  onDisconnected?: () => void;
  onE2EEStatusChanged?: (enabled: boolean) => void;
}

export type ConnectionQualityLevel = "excellent" | "good" | "poor" | "unknown";

export interface UseVideoRoomReturn {
  room: Room | null;
  connectionState: ConnectionStatus;
  connectionQuality: ConnectionQualityLevel;
  localParticipant: LocalParticipant | null;
  remoteParticipants: RemoteParticipant[];
  spotlightParticipant: Participant | null;
  /** Whether E2E encryption is currently active */
  isE2EEEnabled: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  error: Error | null;
}

function safeDisconnectRoom(targetRoom: Room | null) {
  if (!targetRoom) return;
  try {
    targetRoom.disconnect();
  } catch (err) {
    console.warn("Room disconnect failed during cleanup:", err);
  }
}

export function useVideoRoom(options: UseVideoRoomOptions): UseVideoRoomReturn {
  const {
    serverUrl,
    token,
    role,
    forceLegacySignalPath = true,
    e2ee,
    preferHighQuality = false,
    onParticipantJoined,
    onParticipantLeft,
    onDisconnected,
    onE2EEStatusChanged,
  } = options;

  const [room, setRoom] = useState<Room | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionStatus>("disconnected");
  const [remoteParticipants, setRemoteParticipants] = useState<
    RemoteParticipant[]
  >([]);
  const [error, setError] = useState<Error | null>(null);
  const [isE2EEEnabled, setIsE2EEEnabled] = useState<boolean>(false);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQualityLevel>("unknown");

  // Keep key provider ref for potential key rotation
  const keyProviderRef = useRef<ExternalE2EEKeyProvider | null>(null);
  // Keep E2EE worker ref for cleanup (prevents zombie workers blocking page unload)
  const e2eeWorkerRef = useRef<Worker | null>(null);
  // Generation counter: incremented on cleanup/disconnect to invalidate in-flight connects
  const generationRef = useRef(0);
  const roomRef = useRef<Room | null>(null);

  // Determine spotlight based on role
  const getSpotlightParticipant = useCallback(
    (participants: RemoteParticipant[]): Participant | null => {
      if (participants.length === 0) return null;

      // Find participant by role metadata
      // Gutachter sees Versicherter as spotlight
      // Versicherter sees Gutachter as spotlight
      // Guest sees Versicherter as spotlight
      const targetRole = role === "gutachter" ? "versicherter" : "gutachter";

      const spotlight = participants.find((p) => {
        try {
          const metadata = JSON.parse(p.metadata || "{}");
          return metadata.role === targetRole;
        } catch {
          return false;
        }
      });

      // Fallback to first participant if role not found
      return spotlight || participants[0];
    },
    [role]
  );

  const connect = useCallback(async () => {
    if (roomRef.current?.state === ConnectionState.Connected) {
      return;
    }

    // Capture current generation to detect stale completions
    const myGeneration = generationRef.current;

    setConnectionState("connecting");
    setError(null);
    setIsE2EEEnabled(false);

    // Disconnect previous room if any
    if (roomRef.current) {
      safeDisconnectRoom(roomRef.current);
      roomRef.current = null;
    }

    try {
      // Set up E2EE if configured
      let keyProvider: ExternalE2EEKeyProvider | undefined;
      let e2eeWorker: Worker | undefined;

      if (e2ee?.sharedKey) {
        keyProvider = new ExternalE2EEKeyProvider();
        keyProviderRef.current = keyProvider;

        // Convert shared key to ArrayBuffer (key must be at least 32 bytes for AES-256)
        const keyMaterial = new TextEncoder().encode(e2ee.sharedKey.padEnd(32, "0"));
        await keyProvider.setKey(keyMaterial.buffer as ArrayBuffer);

        // Create E2EE worker - try multiple approaches for compatibility
        // Approach 1: Try loading from public folder (most reliable for Vite)
        const workerUrl = e2ee.workerUrl || "/livekit-e2ee-worker.js";

        // Terminate any previous E2EE worker before creating a new one
        if (e2eeWorkerRef.current) {
          e2eeWorkerRef.current.terminate();
          e2eeWorkerRef.current = null;
        }

        try {
          e2eeWorker = new Worker(workerUrl, { type: "module" });
          e2eeWorkerRef.current = e2eeWorker;
          console.log("E2EE worker loaded from:", workerUrl);
        } catch (workerError) {
          console.warn("E2EE worker load failed:", workerError);

          // Approach 2: Try dynamic import URL
          try {
            e2eeWorker = new Worker(
              new URL("livekit-client/e2ee-worker", import.meta.url),
              { type: "module" }
            );
            e2eeWorkerRef.current = e2eeWorker;
            console.log("E2EE worker loaded via dynamic import");
          } catch (fallbackError) {
            console.error("E2EE worker failed to load:", fallbackError);
            // E2EE will not be enabled
          }
        }
      }

      // Create room with E2EE options if configured
      const roomOptions: ConstructorParameters<typeof Room>[0] = {
        adaptiveStream: true,
        dynacast: true,
        // Capture at 1080p/30fps for maximum clarity in medical assessments.
        // Adaptive streaming + simulcast will downscale automatically on
        // poor connections.
        videoCaptureDefaults: {
          resolution: VideoPresets.h1080.resolution,
        },
        // Custom high-bitrate 1080p encoding (6 Mbps) for crisp medical
        // assessment video. The default h1080 preset only allows 3 Mbps
        // which is too conservative. VP9 + simulcast ensure graceful
        // degradation on poor connections.
        publishDefaults: {
          videoEncoding: {
            maxBitrate: 6_000_000,
            maxFramerate: 30,
          },
          videoCodec: "vp9" as VideoCodec,
          simulcast: true,
          screenShareEncoding: {
            maxBitrate: 8_000_000,
            maxFramerate: 30,
          },
        },
      };

      // NOTE: Do NOT set singlePeerConnection = false. The OpenVidu server
      // works with the default single-PeerConnection mode (singlePeerConnection: true).
      // Setting it to false switches to dual-PeerConnection mode which breaks
      // track publishing (camera/mic fail silently).

      if (keyProvider && e2eeWorker) {
        roomOptions.e2ee = {
          keyProvider,
          worker: e2eeWorker,
        };
      }

      const newRoom = new Room(roomOptions);

      // Check if this connect was invalidated during async setup
      if (generationRef.current !== myGeneration) {
        safeDisconnectRoom(newRoom);
        return;
      }

      // Set up event listeners
      newRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
        // Ignore events from stale rooms
        if (generationRef.current !== myGeneration) return;
        switch (state) {
          case ConnectionState.Connected:
            setConnectionState("connected");
            break;
          case ConnectionState.Reconnecting:
            setConnectionState("reconnecting");
            break;
          case ConnectionState.Disconnected:
            setConnectionState("disconnected");
            setIsE2EEEnabled(false);
            onDisconnected?.();
            break;
        }
      });

      newRoom.on(RoomEvent.ParticipantConnected, (participant) => {
        if (generationRef.current !== myGeneration) return;
        setRemoteParticipants((prev) => [...prev, participant]);
        onParticipantJoined?.(participant);
      });

      newRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
        if (generationRef.current !== myGeneration) return;
        setRemoteParticipants((prev) =>
          prev.filter((p) => p.sid !== participant.sid)
        );
        onParticipantLeft?.(participant);
      });

      newRoom.on(RoomEvent.Disconnected, () => {
        if (generationRef.current !== myGeneration) return;
        setConnectionState("disconnected");
        setRemoteParticipants([]);
        setIsE2EEEnabled(false);
        onDisconnected?.();
      });

      // Listen for E2EE status changes
      newRoom.on(RoomEvent.EncryptionError, (err) => {
        if (generationRef.current !== myGeneration) return;
        console.error("E2EE encryption error:", err);
        setError(new Error(`E2EE error: ${err.message}`));
      });

      // Track local connection quality
      newRoom.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
        if (generationRef.current !== myGeneration) return;
        // Only track local participant quality
        if (participant.isLocal) {
          switch (quality) {
            case ConnectionQuality.Excellent:
              setConnectionQuality("excellent");
              break;
            case ConnectionQuality.Good:
              setConnectionQuality("good");
              break;
            case ConnectionQuality.Poor:
              setConnectionQuality("poor");
              break;
            default:
              setConnectionQuality("unknown");
          }
        }
      });

      // Connect with metadata
      await newRoom.connect(serverUrl, token, {
        autoSubscribe: true,
      });

      // Check again after async connect
      if (generationRef.current !== myGeneration) {
        safeDisconnectRoom(newRoom);
        return;
      }

      // Enable E2EE after connection if configured
      if (e2ee?.sharedKey && keyProvider) {
        try {
          await newRoom.setE2EEEnabled(true);
          setIsE2EEEnabled(true);
          onE2EEStatusChanged?.(true);
          console.log("E2E encryption enabled successfully");
        } catch (e2eeError) {
          console.error("Failed to enable E2EE:", e2eeError);
          // Don't fail the connection, but report the error
          setError(new Error(`E2EE activation failed: ${e2eeError instanceof Error ? e2eeError.message : "Unknown error"}`));
        }
      }

      // Set local participant metadata
      await newRoom.localParticipant.setMetadata(JSON.stringify({ role }));

      // Final staleness check
      if (generationRef.current !== myGeneration) {
        safeDisconnectRoom(newRoom);
        return;
      }

      // Initialize remote participants
      const participants = Array.from(newRoom.remoteParticipants.values());
      setRemoteParticipants(participants);

      roomRef.current = newRoom;
      setRoom(newRoom);
      setConnectionState("connected");
    } catch (err) {
      if (generationRef.current !== myGeneration) return;
      setError(err instanceof Error ? err : new Error("Connection failed"));
      setConnectionState("disconnected");
      setIsE2EEEnabled(false);
    }
  }, [serverUrl, token, role, forceLegacySignalPath, e2ee?.sharedKey, preferHighQuality, onParticipantJoined, onParticipantLeft, onDisconnected, onE2EEStatusChanged]);

  const disconnect = useCallback(() => {
    generationRef.current++;
    safeDisconnectRoom(roomRef.current);
    roomRef.current = null;
    if (e2eeWorkerRef.current) {
      e2eeWorkerRef.current.terminate();
      e2eeWorkerRef.current = null;
    }
    setRoom(null);
    setConnectionState("disconnected");
    setConnectionQuality("unknown");
    setIsE2EEEnabled(false);
    setRemoteParticipants([]);
    onDisconnected?.();
  }, [onDisconnected]);

  // Eagerly tear down on page unload (beforeunload fires BEFORE React cleanup).
  // Without this, the browser waits for the WebSocket close handshake and the
  // E2EE worker to terminate — which never completes cleanly during a refresh,
  // causing the page to freeze / hang on reload.
  useEffect(() => {
    const teardown = () => {
      generationRef.current++;
      safeDisconnectRoom(roomRef.current);
      roomRef.current = null;
      if (e2eeWorkerRef.current) {
        e2eeWorkerRef.current.terminate();
        e2eeWorkerRef.current = null;
      }
    };

    window.addEventListener("beforeunload", teardown);
    return () => {
      window.removeEventListener("beforeunload", teardown);
      teardown();
    };
  }, []);

  return {
    room,
    connectionState,
    connectionQuality,
    localParticipant: room?.localParticipant || null,
    remoteParticipants,
    spotlightParticipant: getSpotlightParticipant(remoteParticipants),
    isE2EEEnabled,
    connect,
    disconnect,
    error,
  };
}
